import type {Client, ContentRelationshipField} from '@prismicio/client'
import {filter, isFilled, NotFoundError} from '@prismicio/client'
import type {GetStaticProps, PreviewData} from 'next'
import {createClient} from 'prismicio'
import type {
  ArticleDocument,
  BlogPostDocument,
  ButtonDocument,
  CustomerStoryDocument,
  DialogDocumentData,
  DialogSlice,
  EventDocument,
  FormSectionSlice,
  GenericFormDocument,
  GlossaryPageDocument,
  GlossaryTermDocument,
  NavigationDocumentData,
  PageDocument,
  PageDocumentDataSlicesSlice,
  ProductAnnouncementCardDocument,
  ProductDeepDiveDocument,
  ProductDeepDiveDocumentData,
  ProductDeepDiveSlice,
  ProductDeepDiveVerticalScrollDocument,
} from 'prismicio-types'
import type {ComponentProps} from 'react'
import {POSTS_PER_PAGE} from 'slices/BlogPostsContainer/utils'
import {PageRouter as Page} from 'src/PageRouter'
import {NAVIGATION_FETCH_LINKS, NAVIGATION_QUERY} from 'src/common/constants'
import {wrongUids} from 'src/common/helpers'
import {logger} from 'src/common/logger'
import {formatPathForLog, redactEmails} from 'src/common/logger/formatPathForLog'
import {fetchArticlesIfSlicePresent} from 'src/common/utils/articleRoutes'
import {
  BLOG_POST_GRAPH_QUERY,
  fetchBlogListingCommonData,
  findCategoryByLocalizedSlug,
  generateCategoriesFromPosts,
} from 'src/common/utils/blogRoutes'
import {
  fetchAllCustomerStories,
  fetchCustomerStoriesData,
  fetchCustomerStoriesIfSlicePresent,
  resolveCustomerStoriesV2Links,
} from 'src/common/utils/customerStoryRoutes'
import type {ParentUid} from 'src/common/utils/documentHelpers'
import {EVENT_GRAPH_QUERY, selectRelatedEvents} from 'src/common/utils/eventRoutes'
import {generateFinanceGuideCategoryMetadata} from 'src/common/utils/financeGuideMetadata'
import {
  buildPagePathMap,
  buildResolvedRouteTable,
  getAllPagesForLocale,
  RESERVED_TOP_LEVEL_SEGMENTS,
} from 'src/common/utils/pageSlugResolver'
import {PrismicLookupError} from 'src/common/utils/prismicErrors'

export default Page

type RouteProps = ComponentProps<typeof Page>

const DOCUMENT_FETCH_LINKS = [
  'page.parent_page',
  'page.parent_page.alternate_languages',
  'page.meta_title',
  'page.breadcrumbs',
  'page.computed_path',
  'article.tag',
  'article.title',
  'article.author',
  'article.poster',
  'article.parent_page',
  'author.photo',
  'author.name',
  'author.summary',
  'author.linkedin_link',
  'partner_category.name',
  'blog_post.title',
  'blog_post.description',
  'blog_post.meta_image',
  'blog_post.category',
  'blog_post.date',
  'blog_post.author',
  'blog_post.parent_page',
  'event.meta_description',
  'event.title',
  'event.event_type',
  'event.event_date',
  'event.language',
  'event.location',
  'event.hero_image',
  'event.registerButton',
  'customer_story.parent_page',
  'customer_story.image',
  'customer_story.title',
  'customer_story.tag',
  'customer_story.company_logo',
  'industry.name',
  'industry.icon',
  'glossary_page.uid',
  'glossary_page.title',
  'glossary_page.breadcrumbs',
  'glossary_page.related_terms_heading',
  'button.label',
  'button.link',
  'button.marketing_id',
  'image_logo_set.images',
]

function groupByLang<T extends {lang: string}>(docs: T[]): Record<string, T[]> {
  const grouped: Record<string, T[]> = {}

  docs.forEach((doc) => {
    ;(grouped[doc.lang] ??= []).push(doc)
  })

  return grouped
}

export const getStaticPaths = async () => {
  const client = createClient()

  const [pages, glossaryPages, articles, customerStories, blogPosts, glossaryTerms, events] = await Promise.all([
    client.getAllByType('page', {lang: '*'}),
    client.getAllByType('glossary_page', {lang: '*'}),
    client.getAllByType('article', {lang: '*'}),
    client.getAllByType('customer_story', {lang: '*'}),
    client.getAllByType('blog_post', {lang: '*'}),
    client.getAllByType('glossary_term', {lang: '*'}),
    client.getAllByType('event', {lang: '*'}),
  ])

  const pagesByLang = groupByLang(pages)
  const glossaryPagesByLang = groupByLang(glossaryPages)
  const articlesByLang = groupByLang(articles)
  const customerStoriesByLang = groupByLang(customerStories)
  const blogPostsByLang = groupByLang(blogPosts)
  const glossaryTermsByLang = groupByLang(glossaryTerms)
  const eventsByLang = groupByLang(events)

  const locales = new Set([
    ...Object.keys(pagesByLang),
    ...Object.keys(glossaryPagesByLang),
    ...Object.keys(articlesByLang),
    ...Object.keys(customerStoriesByLang),
    ...Object.keys(blogPostsByLang),
    ...Object.keys(glossaryTermsByLang),
    ...Object.keys(eventsByLang),
  ])

  const paths: Array<{params: {path: string[]}; locale: string}> = []

  locales.forEach((locale) => {
    const table = buildResolvedRouteTable({
      pages: pagesByLang[locale] ?? [],
      glossaryPages: glossaryPagesByLang[locale] ?? [],
      articles: articlesByLang[locale] ?? [],
      customerStories: customerStoriesByLang[locale] ?? [],
      blogPosts: blogPostsByLang[locale] ?? [],
      glossaryTerms: glossaryTermsByLang[locale] ?? [],
      events: eventsByLang[locale] ?? [],
      locale,
    })

    Array.from(table.keys()).forEach((path) => {
      paths.push({params: {path: path.split('/').filter(Boolean)}, locale})
    })
  })

  return {
    paths,
    fallback: 'blocking',
  }
}

type NotFoundLevel = 'debug' | 'info' | 'warn'

type NotFoundLog = {
  level: NotFoundLevel
  reason: string
  locale: string | undefined
  pathSegments: string[]
}

function logNotFound({level, reason, locale, pathSegments}: NotFoundLog): null {
  logger[level]('Page not found', {
    route: '[...path]',
    reason,
    locale: locale ?? null,
    path: formatPathForLog(pathSegments),
    depth: pathSegments.length,
  })

  return null
}

export const getStaticProps: GetStaticProps<RouteProps, {path: string[]}> = async ({params, locale, previewData}) => {
  const pathSegments = params?.path ?? []

  if (pathSegments.some((segment) => wrongUids.some((badSegment) => segment.includes(badSegment)))) {
    logNotFound({level: 'debug', reason: 'blockedExtension', locale, pathSegments})

    return {notFound: true}
  }

  if (!locale) {
    logNotFound({level: 'warn', reason: 'missingLocale', locale, pathSegments})

    return {notFound: true}
  }

  try {
    const props = await resolvePathToProps(pathSegments, locale, previewData)

    if (!props) {
      return {notFound: true}
    }

    return {
      props,
      revalidate: Number(process.env.REVALIDATE_TIME) || 300,
    }
  } catch (error) {
    if (error instanceof NotFoundError) {
      logNotFound({level: 'warn', reason: 'prismicNotFound', locale, pathSegments})

      return {notFound: true}
    }

    const lookupContext = error instanceof PrismicLookupError ? {type: error.type, uid: redactEmails(error.uid)} : {}

    logger.error(
      'Unable to get data from Prismic',
      {
        route: '[...path]',
        path: formatPathForLog(pathSegments),
        locale,
        isPreview: Boolean(previewData),
        ...lookupContext,
      },
      error,
    )

    throw new Error('Unable to get data from Prismic', {cause: error})
  }
}

type BlogListingSuffix =
  | {kind: 'pagination'; ancestorSegments: string[]; pageNumber: number}
  | {kind: 'categoryIndex'; ancestorSegments: string[]; categorySlug: string}
  | {kind: 'categoryPage'; ancestorSegments: string[]; categorySlug: string; pageNumber: number}

function parseBlogListingSuffix(pathSegments: string[]): BlogListingSuffix | null {
  const isPageNumber = (value: string | undefined): value is string => value !== undefined && /^\d+$/.test(value)
  const last = pathSegments[pathSegments.length - 1]
  const secondLast = pathSegments[pathSegments.length - 2]
  const thirdLast = pathSegments[pathSegments.length - 3]

  if (secondLast === 'page' && isPageNumber(last)) {
    return {kind: 'pagination', ancestorSegments: pathSegments.slice(0, -2), pageNumber: Number(last)}
  }

  if (thirdLast === 'category' && isPageNumber(last)) {
    return {
      kind: 'categoryPage',
      ancestorSegments: pathSegments.slice(0, -3),
      categorySlug: secondLast,
      pageNumber: Number(last),
    }
  }

  if (secondLast === 'category') {
    return {kind: 'categoryIndex', ancestorSegments: pathSegments.slice(0, -2), categorySlug: last}
  }

  return null
}

const PAGE_CHILD_LEAF_TYPES = ['article', 'customer_story', 'blog_post', 'event'] as const

async function tryGetByUID<T extends Parameters<Client['getByUID']>[0]>(
  client: Client,
  type: T,
  uid: string,
  locale: string,
) {
  try {
    return await client.getByUID(type, uid, {lang: locale})
  } catch (error) {
    if (error instanceof NotFoundError) {
      return null
    }

    throw new PrismicLookupError(type, uid, locale, error)
  }
}

async function findLeafUnderPage(client: Client, uid: string, ancestorPageId: string, locale: string) {
  const candidates = await Promise.all(PAGE_CHILD_LEAF_TYPES.map((type) => tryGetByUID(client, type, uid, locale)))

  return (
    candidates.find(
      (candidate): candidate is NonNullable<typeof candidate> =>
        candidate !== null &&
        isFilled.contentRelationship(candidate.data.parent_page) &&
        candidate.data.parent_page.id === ancestorPageId,
    ) ?? null
  )
}

function isReservedRootRequest(pathSegments: string[]): boolean {
  return pathSegments.length === 1 && RESERVED_TOP_LEVEL_SEGMENTS.includes(pathSegments[0])
}

async function resolvePathToProps(
  pathSegments: string[],
  locale: string,
  previewData: PreviewData | undefined,
): Promise<RouteProps | null> {
  const client = createClient({previewData})
  const pages = await getAllPagesForLocale(locale, previewData)
  const pagePathMap = buildPagePathMap(pages)
  const fullPath = `/${pathSegments.join('/')}`

  const logAndReturnNull = (reason: string, level: NotFoundLevel = 'info'): null =>
    logNotFound({level, reason, locale, pathSegments})

  const buildDocumentPropsOrLog = async (docId: string): Promise<RouteProps | null> =>
    (await buildDocumentProps(docId, locale, previewData)) ?? logAndReturnNull('unsupportedDocType', 'warn')

  const pageMatch = pagePathMap.get(fullPath)

  if (pageMatch && !isReservedRootRequest(pathSegments)) {
    return buildDocumentPropsOrLog(pageMatch.id)
  }

  if (pathSegments.length === 1) {
    const glossaryPage = await tryGetByUID(client, 'glossary_page', pathSegments[0], locale)

    return glossaryPage ? buildDocumentPropsOrLog(glossaryPage.id) : logAndReturnNull('noRouteMatch')
  }

  const blogListingSuffix = parseBlogListingSuffix(pathSegments)

  if (blogListingSuffix) {
    const ancestorPage = pagePathMap.get(`/${blogListingSuffix.ancestorSegments.join('/')}`)

    const hasBlogListing = ancestorPage?.data.slices.some(
      (slice: PageDocumentDataSlicesSlice) => slice.slice_type === 'blog_posts_container',
    )

    if (!ancestorPage || !hasBlogListing) {
      return logAndReturnNull('blogListingMissing')
    }

    if (blogListingSuffix.kind === 'pagination') {
      return (
        (await buildPlainPaginationProps(ancestorPage.id, blogListingSuffix.pageNumber, locale, previewData)) ??
        logAndReturnNull('paginationOutOfRange')
      )
    }

    const categoryName = findCategoryByLocalizedSlug(blogListingSuffix.categorySlug, locale)

    if (!categoryName) {
      return logAndReturnNull('blogCategoryMissing')
    }

    if (blogListingSuffix.kind === 'categoryIndex') {
      return buildCategoryIndexProps(ancestorPage.id, categoryName, locale, previewData)
    }

    return (
      (await buildCategoryPaginationProps(
        ancestorPage.id,
        categoryName,
        blogListingSuffix.pageNumber,
        locale,
        previewData,
      )) ?? logAndReturnNull('paginationOutOfRange')
    )
  }

  const leafUid = pathSegments[pathSegments.length - 1]
  const ancestorSegments = pathSegments.slice(0, -1)
  const ancestorPage = pagePathMap.get(`/${ancestorSegments.join('/')}`)

  if (ancestorPage) {
    const leafDoc = await findLeafUnderPage(client, leafUid, ancestorPage.id, locale)

    return leafDoc ? buildDocumentPropsOrLog(leafDoc.id) : logAndReturnNull('leafNotFound')
  }

  if (ancestorSegments.length === 1) {
    const glossaryPage = await tryGetByUID(client, 'glossary_page', ancestorSegments[0], locale)
    const term = glossaryPage ? await tryGetByUID(client, 'glossary_term', leafUid, locale) : null

    if (term && isFilled.contentRelationship(term.data.parent_page) && term.data.parent_page.id === glossaryPage?.id) {
      return buildDocumentPropsOrLog(term.id)
    }
  }

  return logAndReturnNull('noRouteMatch')
}

async function computeParentUids(client: Client, parentPage: ContentRelationshipField): Promise<ParentUid[]> {
  if (!isFilled.contentRelationship(parentPage)) {
    return []
  }

  const parentDocument = await client.getByID(parentPage.id, {
    lang: '*',
    fetch: 'page.__nonexistent-field__',
  })

  const altDocs = await client.getAllByIDs(
    parentDocument.alternate_languages.map((altLang) => altLang.id),
    {lang: '*', fetch: 'page.__nonexistent-field__'},
  )

  return [parentDocument, ...altDocs].map((doc) => ({uid: doc.uid, lang: doc.lang}))
}

async function fetchRelatedEvents(
  client: Client,
  currentEvent: EventDocument,
  locale: string,
): Promise<EventDocument[]> {
  const currentLanguage = currentEvent.data.language
  const filters = [filter.not('document.id', currentEvent.id)]

  if (currentLanguage) {
    filters.push(filter.at('my.event.language', currentLanguage))
  }

  const otherEvents = await client.getAllByType<EventDocument>('event', {
    lang: locale,
    graphQuery: EVENT_GRAPH_QUERY,
    orderings: {field: 'my.event.event_date', direction: 'asc'},
    filters,
  })

  return selectRelatedEvents(otherEvents, currentEvent.data.event_type, new Date())
}

async function buildDocumentProps(
  docId: string,
  locale: string,
  previewData: PreviewData | undefined,
): Promise<RouteProps | null> {
  const client = createClient({previewData})

  const [navigation, buttonsResult, doc, customerStoriesData] = await Promise.all([
    client.getSingle('navigation', {
      fetchLinks: NAVIGATION_FETCH_LINKS,
      lang: locale,
      graphQuery: NAVIGATION_QUERY,
    }),
    client.getAllByType<ButtonDocument>('button', {lang: locale}),
    client.getByID(docId, {lang: locale, fetchLinks: DOCUMENT_FETCH_LINKS}),
    fetchCustomerStoriesData(client, locale),
  ])

  const navigationData = navigation.data
  const buttonsData = buttonsResult as ButtonDocument[]

  if (doc.type === 'page') {
    const page = doc as PageDocument

    page.data.slices = (await resolveCustomerStoriesV2Links(client, page.data.slices)) as typeof page.data.slices

    const hasMagazineSlice = page.data.slices.some((slice: {slice_type: string}) => slice.slice_type === 'magazine')

    const [articles, customerStories] = await Promise.all([
      hasMagazineSlice
        ? fetchArticlesIfSlicePresent(client, page.data.slices, locale, ['magazine'])
        : fetchArticlesIfSlicePresent(client, page.data.slices, locale, ['latest_articles'], 3),
      fetchCustomerStoriesIfSlicePresent(client, page.data.slices, locale),
    ])

    if (!isFilled.contentRelationship(page.data.parent_page)) {
      return buildRootPageProps(
        client,
        page,
        navigationData,
        buttonsData,
        locale,
        articles,
        customerStories,
        customerStoriesData,
      )
    }

    const parentUids = await computeParentUids(client, page.data.parent_page)

    return {
      navigationData,
      page,
      parentUids,
      ...(buttonsData.length > 0 ? {buttonsData} : {}),
      ...(articles.length > 0 ? {articles} : {}),
      ...(customerStories.length > 0 ? {customerStories} : {}),
      ...(customerStoriesData.length > 0 ? {customerStoriesData} : {}),
    }
  }

  if (doc.type === 'glossary_page') {
    const page = doc as GlossaryPageDocument

    const glossaryTerms = await client.getAllByType('glossary_term', {
      lang: locale,
      orderings: {field: 'my.glossary_term.term', direction: 'asc'},
    })

    return {navigationData, page, glossaryTerms}
  }

  if (doc.type === 'article') {
    const page = doc as ArticleDocument

    const [parentUids, articles] = await Promise.all([
      computeParentUids(client, page.data.parent_page),
      fetchArticlesIfSlicePresent(
        client,
        page.data.slices as Array<{slice_type: string}>,
        locale,
        ['latest_articles'],
        3,
      ),
    ])

    return {
      navigationData,
      page,
      parentUids,
      ...(buttonsData.length > 0 ? {buttonsData} : {}),
      ...(articles.length > 0 ? {articles} : {}),
    }
  }

  if (doc.type === 'customer_story') {
    const page = doc as CustomerStoryDocument

    const [parentUids, articles, allStories] = await Promise.all([
      computeParentUids(client, page.data.parent_page),
      fetchArticlesIfSlicePresent(
        client,
        page.data.slices as Array<{slice_type: string}>,
        locale,
        ['latest_articles'],
        3,
      ),
      fetchAllCustomerStories(client, locale),
    ])

    const sameCategoryStories = allStories.filter(
      (story) => (story.data.category as string) === page.data.category && story.id !== page.id,
    )

    const moreCustomerStories = [...sameCategoryStories]

    if (sameCategoryStories.length !== 3) {
      allStories.forEach((story) => {
        const isNotSameStory = story.id !== page.id
        const isNotSameCategory = story.data.category !== page.data.category

        if (isNotSameStory && isNotSameCategory && moreCustomerStories.length < 3) {
          moreCustomerStories.push(story)
        }
      })
    }

    return {
      navigationData,
      page,
      parentUids,
      moreCustomerStories,
      ...(buttonsData.length > 0 ? {buttonsData} : {}),
      ...(articles.length > 0 ? {articles} : {}),
    }
  }

  if (doc.type === 'blog_post') {
    const page = doc as BlogPostDocument
    const parentUids = await computeParentUids(client, page.data.parent_page)

    return {navigationData, page, parentUids, ...(buttonsData.length > 0 ? {buttonsData} : {})}
  }

  if (doc.type === 'glossary_term') {
    const page = doc as GlossaryTermDocument
    const parentUids = await computeParentUids(client, page.data.parent_page)

    let relatedTerms: Array<GlossaryTermDocument> = []

    if (isFilled.group(page.data.related_terms) && Array.isArray(page.data.related_terms)) {
      const relatedTermIds = page.data.related_terms
        .map((item) => (isFilled.contentRelationship(item.term) && isFilled.link(item.term) ? item.term.id : null))
        .filter((id): id is string => typeof id === 'string')

      if (relatedTermIds.length > 0) {
        relatedTerms = await client.getAllByIDs<GlossaryTermDocument>(relatedTermIds, {lang: locale})
      }
    }

    return {
      navigationData,
      page,
      parentUids,
      ...(buttonsData.length > 0 ? {buttonsData} : {}),
      ...(relatedTerms.length > 0 ? {relatedTerms} : {}),
    }
  }

  if (doc.type === 'event') {
    const page = doc as EventDocument
    const parentUids = await computeParentUids(client, page.data.parent_page)
    const relatedEvents = await fetchRelatedEvents(client, page, locale)

    return {
      navigationData,
      page,
      parentUids,
      ...(buttonsData.length > 0 ? {buttonsData} : {}),
      ...(relatedEvents.length > 0 ? {relatedEvents} : {}),
    }
  }

  return null
}

async function buildRootPageProps(
  client: Client,
  page: PageDocument,
  navigationData: NavigationDocumentData,
  buttonsData: ButtonDocument[],
  locale: string,
  articles: Array<ArticleDocument>,
  customerStories: Array<CustomerStoryDocument>,
  customerStoriesData: Array<CustomerStoryDocument>,
): Promise<RouteProps> {
  let blogPosts: BlogPostDocument<string>[] = []
  let categories: Array<{category_name: string; value: string}> = []
  let totalPages: number | undefined
  let formData: GenericFormDocument | undefined
  let dialogData: DialogDocumentData | undefined
  let productAnnouncementsData
  let productDeepDiveData: ProductDeepDiveDocumentData | undefined

  if (page.data.slices.find((slice: PageDocumentDataSlicesSlice) => slice.slice_type === 'blog_posts_container')) {
    const allBlogPosts = await client.getAllByType<BlogPostDocument<string>>('blog_post', {
      lang: locale,
      fetchLinks: ['page.computed_path'],
    })

    blogPosts = allBlogPosts.sort((a, b) => {
      const dateA = a.data.date ?? a.first_publication_date ?? ''
      const dateB = b.data.date ?? b.first_publication_date ?? ''

      return dateB.localeCompare(dateA)
    })

    totalPages = Math.ceil(allBlogPosts.length / POSTS_PER_PAGE)
    categories = generateCategoriesFromPosts(allBlogPosts, locale)
  }

  const form = page.data.slices.find((slice: PageDocumentDataSlicesSlice) => slice.slice_type === 'form_section') as
    FormSectionSlice | undefined

  if (form && isFilled.contentRelationship(form.primary.form_document)) {
    formData = (await client.getByUID('generic_form', form.primary.form_document.uid || '', {
      lang: locale,
    })) as GenericFormDocument
  }

  const dialog = page.data.slices.find((slice: PageDocumentDataSlicesSlice) => slice.slice_type === 'dialog') as
    DialogSlice | undefined

  if (dialog && isFilled.contentRelationship(dialog.primary.dialog_content_id)) {
    dialogData = (await client.getByID(dialog.primary.dialog_content_id.id, {
      lang: locale,
    })) as unknown as DialogDocumentData
  }

  const productAnnouncementsContainer = page.data.slices.find(
    (slice: PageDocumentDataSlicesSlice) => slice.slice_type === 'product_announcements_container',
  )

  if (productAnnouncementsContainer) {
    const data = await client.getAllByType<ProductAnnouncementCardDocument>('product_announcement_card', {lang: locale})

    productAnnouncementsData = data.sort(
      ({data: {date: aDate}}, {data: {date: bDate}}) =>
        Number(new Date(bDate as string)) - Number(new Date(aDate as string)),
    )
  }

  const productDeepDiveSlice = page.data.slices.find(
    (slice: PageDocumentDataSlicesSlice) => slice.slice_type === 'product_deep_dive',
  ) as ProductDeepDiveSlice | undefined

  if (productDeepDiveSlice) {
    let requestedType: ProductDeepDiveDocument['type'] | ProductDeepDiveVerticalScrollDocument['type']
    let documentUid

    switch (productDeepDiveSlice.variation) {
      case 'verticalScroll':
        requestedType = 'product_deep_dive_vertical_scroll'

        documentUid = isFilled.contentRelationship(productDeepDiveSlice.primary.sections)
          ? productDeepDiveSlice.primary.sections.uid!
          : null

        break

      case 'singleSection':
        requestedType = 'product_deep_dive'
        documentUid = null
        break

      default:
        requestedType = 'product_deep_dive'

        documentUid = isFilled.contentRelationship(productDeepDiveSlice.primary.sections)
          ? productDeepDiveSlice.primary.sections.uid!
          : null
    }

    if (documentUid) {
      productDeepDiveData = (await client.getByUID(requestedType, documentUid, {lang: locale}))
        .data as ProductDeepDiveDocumentData
    }
  }

  return {
    navigationData,
    page,
    ...(articles.length > 0 ? {articles} : {}),
    ...(customerStories.length > 0 ? {customerStories} : {}),
    ...(customerStoriesData.length > 0 ? {customerStoriesData} : {}),
    ...(productDeepDiveData && {productDeepDiveData}),
    ...(formData ? {formData} : {}),
    ...(dialogData ? {dialogData} : {}),
    ...(productAnnouncementsData ? {productAnnouncementsData} : {}),
    ...(buttonsData.length > 0 ? {buttonsData} : {}),
    ...(blogPosts.length > 0 ? {blogPosts} : {}),
    ...(categories.length > 0 ? {categories} : {}),
    ...(totalPages === undefined ? {} : {totalPages}),
  }
}

async function buildCategoryIndexProps(
  parentPageId: string,
  categoryName: string,
  locale: string,
  previewData: PreviewData | undefined,
): Promise<RouteProps | null> {
  const client = createClient({previewData})
  const {navigationData, buttonsData, page} = await fetchBlogListingCommonData(parentPageId, locale, previewData)

  const allPosts = await client.getAllByType('blog_post', {lang: locale})

  const blogPostsQuery = await client.getByType<BlogPostDocument>('blog_post', {
    lang: locale,
    page: 1,
    pageSize: POSTS_PER_PAGE,
    filters: [filter.at('my.blog_post.category', categoryName)],
    graphQuery: BLOG_POST_GRAPH_QUERY,
    orderings: {field: 'my.blog_post.date', direction: 'desc'},
  })

  const categories = generateCategoriesFromPosts(allPosts, locale)
  const pageWithDynamicMeta = generateFinanceGuideCategoryMetadata(categoryName, page as PageDocument)

  return {
    navigationData,
    ...(buttonsData.length > 0 ? {buttonsData} : {}),
    page: pageWithDynamicMeta,
    blogPosts: blogPostsQuery.results,
    totalPages: blogPostsQuery.total_pages,
    categories,
  }
}

async function buildCategoryPaginationProps(
  parentPageId: string,
  categoryName: string,
  pageNumber: number,
  locale: string,
  previewData: PreviewData | undefined,
): Promise<RouteProps | null> {
  const client = createClient({previewData})
  const {navigationData, buttonsData, page} = await fetchBlogListingCommonData(parentPageId, locale, previewData)

  const allPosts = await client.getAllByType('blog_post', {lang: locale})

  const blogPostsQuery = await client.getByType<BlogPostDocument>('blog_post', {
    lang: locale,
    page: pageNumber,
    pageSize: POSTS_PER_PAGE,
    filters: [filter.at('my.blog_post.category', categoryName)],
    graphQuery: BLOG_POST_GRAPH_QUERY,
    orderings: {field: 'my.blog_post.date', direction: 'desc'},
  })

  const totalPages = blogPostsQuery.total_pages

  if (pageNumber > totalPages) {
    return null
  }

  const categories = generateCategoriesFromPosts(allPosts, locale)
  const pageWithDynamicMeta = generateFinanceGuideCategoryMetadata(categoryName, page as PageDocument, pageNumber)

  return {
    navigationData,
    ...(buttonsData.length > 0 ? {buttonsData} : {}),
    page: pageWithDynamicMeta,
    blogPosts: blogPostsQuery.results,
    totalPages,
    categories,
  }
}

async function buildPlainPaginationProps(
  parentPageId: string,
  pageNumber: number,
  locale: string,
  previewData: PreviewData | undefined,
): Promise<RouteProps | null> {
  const {navigationData, buttonsData, page} = await fetchBlogListingCommonData(parentPageId, locale, previewData)

  const client = createClient({previewData})

  const blogPostsQuery = await client.getByType<BlogPostDocument>('blog_post', {
    lang: locale,
    page: pageNumber,
    pageSize: POSTS_PER_PAGE,
    graphQuery: BLOG_POST_GRAPH_QUERY,
    orderings: {field: 'my.blog_post.date', direction: 'desc'},
  })

  const totalPages = blogPostsQuery.total_pages

  if (pageNumber > totalPages) {
    return null
  }

  const categories = generateCategoriesFromPosts(blogPostsQuery.results, locale)

  return {
    navigationData,
    ...(buttonsData.length > 0 ? {buttonsData} : {}),
    page: page as PageDocument,
    blogPosts: blogPostsQuery.results,
    totalPages,
    categories,
  }
}
